/* ═══════════════════════════════════════════════════════════════
   TaskComments — Task Sheet's Comments section. Read for anyone who can
   see the task; the comment box only shows with canComment
   (tasks.create, as today — Decision 12).
   ═══════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import type { TaskComment } from '../../../../shared/types';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import EmptyState from '../../../components/EmptyState';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import { MessageSquare } from 'lucide-react';

export interface TaskCommentsProps {
  comments: TaskComment[];
  canComment: boolean;
  getMemberName: (id: string) => string;
  onAdd: (text: string) => Promise<void>;
}

export function TaskComments({ comments, canComment, getMemberName, onAdd }: TaskCommentsProps) {
  const [newComment, setNewComment] = useState('');

  async function handleAddComment() {
    if (!newComment.trim()) return;
    await onAdd(newComment);
    setNewComment('');
  }

  return (
    <div>
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 700,
          color: 'var(--tm)',
          marginBottom: rem(6),
          textTransform: 'uppercase',
          letterSpacing: '.05em',
        }}
      >
        Comments ({comments.length})
      </div>

      {comments.length === 0 ? (
        <EmptyState
          compact
          icon={<Icon icon={MessageSquare} size="md" />}
          title="No comments yet"
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            maxHeight: rem(200),
            overflow: 'auto',
          }}
        >
          {comments.map((c) => (
            <div
              key={c.id}
              style={{
                padding: `var(--space-2) ${rem(10)}`,
                background: 'var(--sa)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-1)',
                }}
              >
                <span
                  style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--tx)' }}
                >
                  {getMemberName(c.authorId)}
                </span>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  {new Date(c.createdAt).toLocaleString()}
                </span>
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--tx)',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {c.text}
              </div>
            </div>
          ))}
        </div>
      )}

      {canComment && (
        <div style={{ display: 'flex', gap: rem(6), marginTop: 'var(--space-2)' }}>
          <Input
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
            placeholder="Write a comment..."
            style={{ flex: 1 }}
          />
          <Button variant="secondary" size="sm" onClick={handleAddComment}>
            Send
          </Button>
        </div>
      )}
    </div>
  );
}
